import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import API from "helpers/api";
import { useMaintenanceBanner } from "contexts/MaintenanceBannerContext";
import {
	googleCalendarUrl,
	outlookCalendarUrl,
	downloadIcs,
} from "helpers/calendarLinks";

// The banner writes its measured height to this CSS var on <html>; layout that
// needs to sit below the banner (the permanent nav Drawer, the release-notes
// page) reads it. Shared so a rename can't silently desync the consumers.
export const BANNER_HEIGHT_VAR = "--maintenance-banner-height";

const HEADING_LABEL = {
	en: "Scheduled maintenance",
	es: "Mantenimiento programado",
};
const DETAILS_LABEL = { en: "Details", es: "Detalles" };
const IN_LABEL = { en: "IN", es: "EN" };
const ENDS_IN_LABEL = { en: "ENDS IN", es: "TERMINA EN" };

const SEVERITY_STYLES = {
	warning: {
		bg: "#FFF4E5",
		fg: "#5A3D1A",
		accent: "#F5A623",
		accentFg: "#FFFFFF",
		border: "rgba(245, 166, 35, 0.25)",
		outline: "rgba(90, 61, 26, 0.45)",
		Icon: WarningAmberRoundedIcon,
	},
	critical: {
		bg: "#FDECEA",
		fg: "#621B16",
		accent: "#D7263D",
		accentFg: "#FFFFFF",
		border: "rgba(215, 38, 61, 0.25)",
		outline: "rgba(98, 27, 22, 0.45)",
		Icon: ErrorOutlineRoundedIcon,
	},
	info: {
		bg: "#E8F1FB",
		fg: "#0A2C5E",
		accent: "#1976D2",
		accentFg: "#FFFFFF",
		border: "rgba(25, 118, 210, 0.25)",
		outline: "rgba(10, 44, 94, 0.45)",
		Icon: InfoOutlinedIcon,
	},
};

function resolveLang() {
	const raw = (navigator.language || "en").slice(0, 2).toLowerCase();
	return raw || "en";
}

function resolveStyles(severity) {
	if (severity === "critical") return SEVERITY_STYLES.critical;
	if (severity === "info") return SEVERITY_STYLES.info;
	return SEVERITY_STYLES.warning;
}

function formatWindowLine(timeZone, start, end, locale) {
	try {
		const dayFmt = new Intl.DateTimeFormat(locale, {
			weekday: "long",
			day: "numeric",
			month: "long",
			year: "numeric",
			timeZone,
		});
		const timeFmt = new Intl.DateTimeFormat(locale, {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
			timeZone,
		});
		const tzFmt = new Intl.DateTimeFormat(locale, {
			timeZoneName: "short",
			timeZone,
		});
		const startDay = dayFmt.format(start);
		const endDay = dayFmt.format(end);
		const startTime = timeFmt.format(start);
		const endTime = timeFmt.format(end);
		const tzPart = tzFmt
			.formatToParts(start)
			.find((p) => p.type === "timeZoneName");
		const tz = tzPart?.value || "";
		const tzSuffix = tz ? " " + tz : "";
		// Show both dates when the window spans more than one day, otherwise the
		// end time reads as if it were on the start day.
		if (startDay === endDay) {
			return `${startDay}, ${startTime} - ${endTime}${tzSuffix}`;
		}
		return `${startDay}, ${startTime} - ${endDay}, ${endTime}${tzSuffix}`;
	} catch (_e) {
		return null;
	}
}

function buildWindowDisplay(banner) {
	const start = banner.windowStart ? new Date(banner.windowStart) : null;
	const end = banner.windowEnd ? new Date(banner.windowEnd) : null;
	if (
		!start ||
		!end ||
		Number.isNaN(start.getTime()) ||
		Number.isNaN(end.getTime())
	) {
		return null;
	}

	const localTz =
		Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
	const locale = navigator.language || "en";

	return {
		text: formatWindowLine(localTz, start, end, locale),
		startMs: start.getTime(),
		endMs: end.getTime(),
	};
}

function formatCountdown(targetMs, nowMs, prefix) {
	const diff = targetMs - nowMs;
	if (diff <= 0) return null;
	const seconds = Math.floor(diff / 1000);
	const days = Math.floor(seconds / 86400);
	const hours = Math.floor((seconds % 86400) / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	if (days > 0) return `${prefix} ${days}d ${hours}h`;
	if (hours > 0) return `${prefix} ${hours}h ${minutes}m`;
	return `${prefix} ${Math.max(minutes, 1)}m`;
}

function MaintenanceBanner() {
	const { banner: data } = useMaintenanceBanner();
	const [, setNowTick] = useState(0);
	const [dismissed, setDismissed] = useState(false);
	const [calAnchor, setCalAnchor] = useState(null);
	const wrapperRef = useRef(null);

	// A newly published/edited banner (different id) un-dismisses locally.
	useEffect(() => {
		setDismissed(false);
	}, [data?.id]);

	const visible = Boolean(data) && !dismissed;

	useEffect(() => {
		if (!visible) return undefined;
		const id = setInterval(() => setNowTick((t) => t + 1), 60 * 1000);
		return () => clearInterval(id);
	}, [visible]);

	useLayoutEffect(() => {
		if (!visible) {
			document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
			return undefined;
		}

		const writeHeight = () => {
			const h = wrapperRef.current?.offsetHeight || 0;
			document.documentElement.style.setProperty(
				BANNER_HEIGHT_VAR,
				`${h}px`
			);
		};

		writeHeight();

		const ro =
			typeof ResizeObserver !== "undefined"
				? new ResizeObserver(writeHeight)
				: null;
		if (ro && wrapperRef.current) ro.observe(wrapperRef.current);
		window.addEventListener("resize", writeHeight);

		return () => {
			window.removeEventListener("resize", writeHeight);
			if (ro) ro.disconnect();
			document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
		};
	}, [visible]);

	if (!visible) return null;

	const banner = {
		message: data.message,
		severity: data.severity,
		windowStart: data.startsAt,
		windowEnd: data.endsAt,
		link: data.link,
	};

	const lang = resolveLang();
	const message = banner.message;
	if (!message) return null;

	const styles = resolveStyles(banner.severity);
	const Icon = styles.Icon;
	const headingLabel = HEADING_LABEL[lang] || HEADING_LABEL.en;
	const detailsLabel = DETAILS_LABEL[lang] || DETAILS_LABEL.en;
	const windowDisplay = buildWindowDisplay(banner);

	let countdownText = null;
	if (windowDisplay) {
		const now = Date.now();
		if (now < windowDisplay.startMs) {
			countdownText = formatCountdown(
				windowDisplay.startMs,
				now,
				IN_LABEL[lang] || IN_LABEL.en
			);
		} else if (now < windowDisplay.endMs) {
			countdownText = formatCountdown(
				windowDisplay.endMs,
				now,
				ENDS_IN_LABEL[lang] || ENDS_IN_LABEL.en
			);
		}
	}

	const handleClose = () => {
		setDismissed(true);
		// Persist the dismissal server-side so it carries across devices.
		API.post("/api/maintenance-banner/dismiss").catch(() => {});
	};

	const calendarEvent = {
		title: "EMI3 scheduled maintenance",
		details: message,
		start: banner.windowStart,
		end: banner.windowEnd,
	};

	const openCalendar = (url) => {
		window.open(url, "_blank", "noopener,noreferrer");
		setCalAnchor(null);
	};

	return (
		<Box
			ref={wrapperRef}
				sx={{
					position: "sticky",
					top: 0,
					// Above the sidebar Drawer (1200), below MUI dialogs (1300).
					zIndex: 1250,
					width: "100%",
					backgroundColor: styles.bg,
					color: styles.fg,
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					px: 2,
					py: 1.25,
					borderBottom: `1px solid ${styles.border}`,
					fontFamily: "inherit",
				}}
			>
				<Box
					aria-hidden
					sx={{
						width: 36,
						height: 36,
						borderRadius: "50%",
						backgroundColor: styles.accent,
						color: styles.accentFg,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					<Icon sx={{ fontSize: 22 }} />
				</Box>
				<Box
					sx={{
						flex: 1,
						display: "flex",
						flexWrap: "wrap",
						columnGap: 1,
						alignItems: "baseline",
						fontSize: 14,
						lineHeight: 1.45,
						minWidth: 0,
					}}
				>
					<Box component="span" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
						{headingLabel}
					</Box>
					<Box component="span" sx={{ opacity: 0.85 }}>
						—
					</Box>
					{windowDisplay?.text ? (
						<Box
							component="span"
							sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
						>
							{windowDisplay.text}.
						</Box>
					) : null}
					<Box component="span" sx={{ opacity: 0.92 }}>
						{message}
					</Box>
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						flexShrink: 0,
					}}
				>
					{countdownText ? (
						<>
							<Box
								component="button"
								type="button"
								onClick={(e) => setCalAnchor(e.currentTarget)}
								title="Add this maintenance window to your calendar"
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 0.5,
									border: `1px solid ${styles.outline}`,
									color: styles.fg,
									backgroundColor: "transparent",
									fontFamily: "inherit",
									fontSize: 12,
									fontWeight: 700,
									letterSpacing: 0.5,
									borderRadius: 1,
									px: 1,
									py: 0.5,
									whiteSpace: "nowrap",
									cursor: "pointer",
									"&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
								}}
							>
								<AccessTimeRoundedIcon sx={{ fontSize: 14 }} />
								{countdownText}
								<ExpandMoreRoundedIcon sx={{ fontSize: 14 }} />
							</Box>
							<Menu
								anchorEl={calAnchor}
								open={Boolean(calAnchor)}
								onClose={() => setCalAnchor(null)}
							>
								<MenuItem onClick={() => openCalendar(googleCalendarUrl(calendarEvent))}>
									Add to Google Calendar
								</MenuItem>
								<MenuItem onClick={() => openCalendar(outlookCalendarUrl(calendarEvent))}>
									Add to Outlook
								</MenuItem>
								<MenuItem
									onClick={() => {
										downloadIcs(calendarEvent);
										setCalAnchor(null);
									}}
								>
									Download .ics (Outlook desktop / Apple)
								</MenuItem>
							</Menu>
						</>
					) : null}
					{banner.link ? (
						<Box
							component="a"
							href={banner.link}
							target="_blank"
							rel="noopener noreferrer"
							sx={{
								border: `1px solid ${styles.outline}`,
								color: styles.fg,
								textDecoration: "none",
								fontWeight: 700,
								fontSize: 12,
								letterSpacing: 0.5,
								textTransform: "uppercase",
								borderRadius: 1,
								px: 1.25,
								py: 0.5,
								whiteSpace: "nowrap",
								"&:hover": {
									backgroundColor: "rgba(0, 0, 0, 0.04)",
								},
							}}
						>
							{detailsLabel}
						</Box>
					) : null}
					<IconButton
						onClick={handleClose}
						size="small"
						aria-label="dismiss"
						sx={{ color: styles.fg, opacity: 0.7 }}
					>
						<CloseRoundedIcon fontSize="small" />
					</IconButton>
				</Box>
		</Box>
	);
}

export default MaintenanceBanner;
